import { APIError } from "web-core/utils";
import {
  purchaseFormValidationSchema,
  redemptionFormValidationSchema,
  sipFormValidationSchema,
} from "creso-core/zod";
import isEmpty from "lodash/isEmpty";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

export function formatError(d: unknown): string {
  if (typeof d === "object" && d !== null) {
    if (Array.isArray(d)) {
      return d.join(", ");
    }
    return JSON.stringify(d);
  }
  if (typeof d === "string") {
    return d;
  }
  return "";
}

type DefaultValue = z.infer<typeof sipFormValidationSchema>;

export function handleSipFormValidationError(
  error: unknown,
  form: UseFormReturn<DefaultValue>,
  index: number
) {
  let inlineErrorSet = false;
  if (error instanceof APIError) {
    const details = error.data.details as Record<string, unknown>;

    if (isEmpty(details)) {
      return inlineErrorSet;
    }

    if ("mandate" in details || "mandate_id" in details) {
      form.setError("mandate", {
        message: formatError(details.mandate || details.mandate_id),
      });
      inlineErrorSet = true;
    }

    if ("start_date" in details) {
      form.setError("start_date", {
        message: formatError(details.start_date),
      });
      inlineErrorSet = true;
    }

    if ("frequency" in details) {
      form.setError("frequency", {
        message: formatError(details.frequency),
      });
      inlineErrorSet = true;
    }

    if ("amount" in details) {
      form.setError(`schemes.${index}.amount`, {
        message: formatError(details.amount),
      });
      inlineErrorSet = true;
    }

    if ("scheme" in details) {
      form.setError(`schemes.${index}.scheme`, {
        message: formatError(details.scheme),
      });
      inlineErrorSet = true;
    }

    if ("folio_id" in details) {
      form.setError(`schemes.${index}.folio_id`, {
        message: formatError(details.scheme),
      });
      inlineErrorSet = true;
    }

    return inlineErrorSet;
  }

  return inlineErrorSet;
}

type PurchaseForm = UseFormReturn<z.infer<typeof purchaseFormValidationSchema>>;

export function handlePurchaseFormValidationError(
  error: unknown,
  form: PurchaseForm,
  index: number
) {
  let inlineErrorSet = false;
  if (error instanceof APIError) {
    const details = error.data.details as Record<string, unknown>;

    if (isEmpty(details)) {
      return inlineErrorSet;
    }

    if ("scheme" in details) {
      form.setError(`schemes.${index}.scheme`, {
        message: formatError(details.scheme),
      });
      inlineErrorSet = true;
    }

    if ("folio_id" in details) {
      form.setError(`schemes.${index}.folio_id`, {
        message: formatError(details.folio_id),
      });
      inlineErrorSet = true;
    }

    if ("amount" in details) {
      form.setError(`schemes.${index}.amount`, {
        message: formatError(details.amount),
      });
      inlineErrorSet = true;
    }

    return inlineErrorSet;
  }
  return inlineErrorSet;
}

type RedemptionForm = UseFormReturn<
  z.infer<typeof redemptionFormValidationSchema>
>;

export function handleRedemptionFormValidationError(
  error: unknown,
  form: RedemptionForm,
  index: number
) {
  let inlineErrorSet = false;
  if (error instanceof APIError) {
    const details = error.data.details as Record<string, unknown>;
    if (isEmpty(details)) {
      return inlineErrorSet;
    }

    if ("scheme_isin" in details) {
      form.setError(`schemes.${index}.scheme`, {
        message: formatError(details.scheme_isin),
      });
      inlineErrorSet = true;
    }

    if ("folio_id" in details) {
      form.setError(`schemes.${index}.folio`, {
        message: formatError(details.folio_id),
      });
      inlineErrorSet = true;
    }

    if ("units" in details) {
      form.setError(`schemes.${index}.units`, {
        message: formatError(details.units),
      });
      inlineErrorSet = true;
    }

    if ("amount" in details) {
      form.setError(`schemes.${index}.amount`, {
        message: formatError(details.amount),
      });
      inlineErrorSet = true;
    }
  }
  return inlineErrorSet;
}
